/**
 * Kawaii Stop — Cart Add-On Quick View V3
 * File: assets/ks-cart-quickview.js
 *
 * Cart-page only. This version intentionally does not use Umino's global
 * Quick View renderer because its global settings were hiding variants and
 * leaving conflicting sale/translation markup inside the cart popup.
 */
(() => {
  "use strict";

  const CART_ROOT = ".ks-cart-page";
  const CARD = ".ks-cart-rec-card";
  const TRIGGERS = [
    ".ks-cart-rec-card__media",
    ".ks-cart-rec-card__title",
    ".ks-cart-rec-card__action",
    ".ks-cart-rec-card__link"
  ].join(", ");
  const MODAL_ID = "ks-cart-quickview-modal";
  const SCROLL_KEY = "ksCartQuickViewScrollY";
  const productCache = new Map();

  let activeProduct = null;
  let activeVariant = null;
  let selectedOptions = [];
  let lastFocusedElement = null;

  function rootUrl() {
    return window.Shopify?.routes?.root || "/";
  }

  function cartAddUrl() {
    return window.routes?.cart_add_url || `${rootUrl()}cart/add.js`;
  }

  function getHandle(card, trigger) {
    const urls = [
      trigger?.getAttribute?.("href"),
      trigger?.dataset?.productUrl,
      card?.dataset?.productUrl,
      card?.querySelector(".ks-cart-rec-card__title")?.getAttribute("href"),
      card?.querySelector(".ks-cart-rec-card__media")?.getAttribute("href")
    ].filter(Boolean);

    for (const value of urls) {
      try {
        const url = new URL(value, window.location.origin);
        const match = url.pathname.match(/\/products\/([^/?#]+)/i);
        if (match?.[1]) return decodeURIComponent(match[1]);
      } catch (_) {
        // Try the next source.
      }
    }

    return "";
  }

  function prepareCards(scope = document) {
    scope.querySelectorAll(`${CART_ROOT} ${CARD}`).forEach((card) => {
      const action = card.querySelector(
        ".ks-cart-rec-card__action, .ks-cart-rec-card__link"
      );
      const media = card.querySelector(".ks-cart-rec-card__media");
      const hasOptions = /choose options/i.test(action?.textContent || "");
      const label = hasOptions ? "View options" : "Quick view";

      if (media) {
        media.classList.add("ks-cart-rec-card__media--quickview");
        media.dataset.ksQuickviewLabel = label;
      }
    });

    scope.querySelectorAll(`${CART_ROOT} ${TRIGGERS}`).forEach((trigger) => {
      trigger.setAttribute("aria-haspopup", "dialog");
      trigger.setAttribute(
        "aria-label",
        "Open product options without leaving your cart"
      );
    });

    scope
      .querySelectorAll(`${CART_ROOT} ${CARD} [data-ks-cart-add]`)
      .forEach((button) => {
        button.type = "button";
        button.textContent = "Quick add";
      });
  }

  function setCardLoading(card, loading) {
    if (!card) return;
    card.classList.toggle("is-quickview-loading", loading);
    card.setAttribute("aria-busy", loading ? "true" : "false");
  }

  async function getProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);

    const request = fetch(
      `${rootUrl()}products/${encodeURIComponent(handle)}.js`,
      {
        credentials: "same-origin",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      }
    ).then(async (response) => {
      if (!response.ok) {
        throw new Error(`Product request failed: ${response.status}`);
      }
      return response.json();
    });

    productCache.set(handle, request);

    try {
      return await request;
    } catch (error) {
      productCache.delete(handle);
      throw error;
    }
  }

  function variantOptions(variant) {
    if (Array.isArray(variant?.options)) {
      return variant.options.map(String);
    }

    return [variant?.option1, variant?.option2, variant?.option3]
      .filter((value) => value !== null && value !== undefined)
      .map(String);
  }

  function productOptions(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const options = Array.isArray(product?.options) ? product.options : [];

    return options.map((option, index) => {
      const position = Number(option?.position || index + 1);
      const name =
        typeof option === "string"
          ? option
          : option?.name || `Option ${position}`;
      const supplied =
        typeof option === "object" && Array.isArray(option?.values)
          ? option.values
          : [];
      const fromVariants = variants
        .map((variant) => variantOptions(variant)[position - 1])
        .filter(Boolean);

      return {
        name,
        position,
        values: Array.from(new Set([...supplied, ...fromVariants].map(String)))
      };
    });
  }

  function money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "USD"
    }).format(Number(cents || 0) / 100);
  }

  function plainDescription(html) {
    if (!html) return "";

    const documentFragment = new DOMParser().parseFromString(
      `<div>${html}</div>`,
      "text/html"
    );

    documentFragment
      .querySelectorAll(
        "script, style, noscript, iframe, form, button, svg, table"
      )
      .forEach((element) => element.remove());

    const firstUsefulBlock = Array.from(
      documentFragment.querySelectorAll("p, li")
    )
      .map((element) => element.textContent.replace(/\s+/g, " ").trim())
      .find((text) => text.length >= 35);

    const text = (firstUsefulBlock || documentFragment.body.textContent || "")
      .replace(/\s+/g, " ")
      .trim();

    if (text.length <= 300) return text;

    const shortened = text.slice(0, 300);
    const sentenceEnd = Math.max(
      shortened.lastIndexOf(". "),
      shortened.lastIndexOf("! "),
      shortened.lastIndexOf("? ")
    );

    return sentenceEnd >= 150
      ? shortened.slice(0, sentenceEnd + 1)
      : `${shortened.replace(/\s+\S*$/, "")}…`;
  }

  function normalizedImageUrl(value) {
    if (!value) return "";
    const stringValue = String(value).trim();
    return stringValue.startsWith("//")
      ? `${window.location.protocol}${stringValue}`
      : stringValue;
  }

  function variantImage(variant, product) {
    const media = variant?.featured_media || null;
    const image = media?.preview_image || variant?.featured_image || null;

    const variantSource = normalizedImageUrl(
      typeof image === "string" ? image : image?.src || image?.url
    );

    const productSource = normalizedImageUrl(
      typeof product?.featured_image === "string"
        ? product.featured_image
        : product?.featured_image?.src ||
            product?.featured_image?.url ||
            (typeof product?.images?.[0] === "string"
              ? product.images[0]
              : product?.images?.[0]?.src || product?.images?.[0]?.url)
    );

    return variantSource || productSource;
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function optionMarkup(product) {
    const options = productOptions(product);

    if ((product.variants || []).length <= 1 || options.length === 0) {
      return "";
    }

    return `
      <div class="product-variant-quick-view ks-cart-qv-variant-host">
        <div class="ks-cart-qv-variants" aria-label="Product options">
          ${options
            .map((option, optionIndex) => {
              const isColor = /colou?r/i.test(option.name);

              return `
                <fieldset class="ks-cart-qv-option-group${
                  isColor ? " is-color" : ""
                }">
                  <legend class="ks-cart-qv-option-group__legend">
                    <span>${escapeHtml(option.name)}: </span>
                    <strong data-ks-selected-option="${optionIndex}"></strong>
                  </legend>
                  <div class="ks-cart-qv-option-group__choices">
                    ${option.values
                      .map(
                        (value) => `
                          <button
                            type="button"
                            class="ks-cart-qv-option${
                              isColor ? " is-color" : ""
                            }"
                            data-option-index="${optionIndex}"
                            data-value="${escapeHtml(value)}"
                            aria-pressed="false"
                          >
                            ${
                              isColor
                                ? `<span class="ks-cart-qv-option__swatch" aria-hidden="true" data-color-name="${escapeHtml(
                                    value
                                  )}"></span>`
                                : ""
                            }
                            <span class="ks-cart-qv-option__label">${escapeHtml(
                              value
                            )}</span>
                          </button>
                        `
                      )
                      .join("")}
                  </div>
                </fieldset>
              `;
            })
            .join("")}
        </div>
      </div>
    `;
  }

  function modalMarkup(product, variant) {
    const price = Number(variant?.price || 0);
    const compareAt = Number(variant?.compare_at_price || 0);
    const onSale = compareAt > price;
    const description = plainDescription(product.description);
    const image = variantImage(variant, product);
    const productUrl = `${rootUrl()}products/${encodeURIComponent(product.handle)}`;

    return `
      <div
        id="${MODAL_ID}"
        class="dlg dialog-open dlg-disable-heading dlg-multi dlg-disable-footer dlg-disable-drag ks-cart-quickview-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ks-cart-qv-title"
      >
        <div class="dlg-box" role="document">
          <div class="dlg-message">
            <button
              type="button"
              class="dlg-close-x"
              data-ks-qv-close
              aria-label="Close product options"
            >
              <span aria-hidden="true">×</span>
            </button>

            <div class="bls__product-quickview bls__product-single bg-white overflow-hidden">
              <div class="row g-0 ks-cart-qv-layout">
                <div class="bls__product-img-quickview col-12 col-lg-6 overflow-hidden relative">
                  <div class="bls__product-main-img">
                    <div class="bls__product-gallery">
                      <div class="swiper-wrapper">
                        <div class="swiper-slide swiper-slide-active">
                          <img
                            class="ks-cart-qv-main-image"
                            src="${escapeHtml(image)}"
                            alt="${escapeHtml(product.title)}"
                            loading="eager"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-12 col-lg-6 relative bls__quickview-content-wap">
                  <div class="bls__quickview-content h-full overflow-x-hidden overflow-y-auto scroll-bar-custom scroll-y">
                    <div class="ks-cart-qv-sale-wrap" ${onSale ? "" : "hidden"}>
                      <div class="bls__sale-label" data-ks-qv-sale>
                        ${onSale ? `Save ${money(compareAt - price)}` : ""}
                      </div>
                    </div>

                    <p class="ks-cart-qv-eyebrow">Complete your cart</p>

                    <h3 id="ks-cart-qv-title" class="bls__product-title">
                      ${escapeHtml(product.title)}
                    </h3>

                    <div class="bls__product-price">
                      <div class="ks-cart-qv-live-price">
                        <span class="ks-cart-qv-live-price__current" data-ks-qv-price>
                          ${money(price)}
                        </span>
                        <s class="ks-cart-qv-live-price__compare" data-ks-qv-compare ${
                          onSale ? "" : "hidden"
                        }>
                          ${onSale ? money(compareAt) : ""}
                        </s>
                      </div>
                    </div>

                    ${
                      description
                        ? `<div class="bls__single-shortdes ks-cart-qv-description">${escapeHtml(
                            description
                          )}</div>`
                        : ""
                    }

                    ${optionMarkup(product)}

                    <p class="ks-cart-qv-availability" data-ks-qv-availability></p>

                    <form action="${escapeHtml(cartAddUrl())}" method="post" class="product-form ks-cart-qv-form">
                      <input type="hidden" name="id" value="${variant.id}">

                      <div class="product-form__buttons flex mb-10 flex-wrap-table">
                        <div class="bls__product-add-cart flex">
                          <div class="product-form__input product-form__quantity mb-15">
                            <div class="quantity btn-radius border bg-grey flex align-items-center text-center mr-10 px-8">
                              <button type="button" class="quantity__button" data-ks-qv-minus aria-label="Decrease quantity">−</button>
                              <input class="quantity__input" type="number" name="quantity" value="1" min="1" step="1" inputmode="numeric">
                              <button type="button" class="quantity__button" data-ks-qv-plus aria-label="Increase quantity">+</button>
                            </div>
                          </div>

                          <button type="submit" class="product-form__submit button button-add-cart-qv">
                            <span>Add to cart</span>
                          </button>
                        </div>
                      </div>
                    </form>

                    <a class="ks-cart-qv-full-details" href="${escapeHtml(
                      productUrl
                    )}">View full product details</a>

                    <div class="bls__product-meta ks-cart-qv-meta">
                      ${
                        product.vendor
                          ? `<div><strong>Vendor:</strong> ${escapeHtml(
                              product.vendor
                            )}</div>`
                          : ""
                      }
                      ${
                        product.type || product.product_type
                          ? `<div><strong>Type:</strong> ${escapeHtml(
                              product.type || product.product_type
                            )}</div>`
                          : ""
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function firstAvailableVariant(product) {
    return (
      product.variants?.find((variant) => variant.available) ||
      product.variants?.[0] ||
      null
    );
  }

  function bestVariant(product, optionIndex, requestedValue) {
    const variants = product.variants || [];
    const available = variants.filter((variant) => variant.available);

    const exact = available.find((variant) =>
      variantOptions(variant).every(
        (value, index) => value === selectedOptions[index]
      )
    );

    if (exact) return exact;

    const candidates = available.filter(
      (variant) => variantOptions(variant)[optionIndex] === requestedValue
    );

    candidates.sort((a, b) => {
      const score = (variant) =>
        variantOptions(variant).reduce(
          (total, value, index) =>
            total + (value === selectedOptions[index] ? 1 : 0),
          0
        );
      return score(b) - score(a);
    });

    return candidates[0] || variants[0] || null;
  }

  function setSwatchColors(modal) {
    modal.querySelectorAll(".ks-cart-qv-option.is-color").forEach((button) => {
      const value = button.dataset.value;
      const swatch = button.querySelector(".ks-cart-qv-option__swatch");
      if (!swatch) return;

      if (window.CSS?.supports?.("color", value)) {
        swatch.style.setProperty("--ks-cart-qv-swatch", value);
      }
    });
  }

  function updateModalVariant(modal, variant) {
    if (!variant) return;

    activeVariant = variant;
    selectedOptions = variantOptions(variant);

    modal.querySelector("input[name='id']").value = variant.id;

    modal.querySelectorAll(".ks-cart-qv-option").forEach((button) => {
      const optionIndex = Number(button.dataset.optionIndex);
      const value = button.dataset.value;
      const active = selectedOptions[optionIndex] === value;
      const exists = activeProduct.variants.some(
        (candidate) =>
          candidate.available &&
          variantOptions(candidate)[optionIndex] === value
      );

      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
      button.disabled = !exists;
    });

    modal.querySelectorAll("[data-ks-selected-option]").forEach((label) => {
      const index = Number(label.dataset.ksSelectedOption);
      label.textContent = selectedOptions[index] || "";
    });

    const price = Number(variant.price || 0);
    const compareAt = Number(variant.compare_at_price || 0);
    const onSale = compareAt > price;
    const currentPrice = modal.querySelector("[data-ks-qv-price]");
    const comparePrice = modal.querySelector("[data-ks-qv-compare]");
    const saleWrap = modal.querySelector(".ks-cart-qv-sale-wrap");
    const saleLabel = modal.querySelector("[data-ks-qv-sale]");

    currentPrice.textContent = money(price);
    comparePrice.hidden = !onSale;
    comparePrice.textContent = onSale ? money(compareAt) : "";
    saleWrap.hidden = !onSale;
    saleLabel.textContent = onSale ? `Save ${money(compareAt - price)}` : "";

    const image = modal.querySelector(".ks-cart-qv-main-image");
    const newImage = variantImage(variant, activeProduct);

    if (image && newImage && image.src !== newImage) {
      image.classList.add("is-changing");
      image.src = newImage;
      image.alt = `${activeProduct.title} — ${selectedOptions.join(" / ")}`;
      image.onload = () => image.classList.remove("is-changing");
      image.onerror = () => image.classList.remove("is-changing");
    }

    const availability = modal.querySelector("[data-ks-qv-availability]");
    const submit = modal.querySelector("[type='submit']");

    availability.classList.toggle("is-unavailable", !variant.available);
    availability.textContent = variant.available
      ? "In stock and ready to add"
      : "Currently unavailable";
    submit.disabled = !variant.available;
    submit.querySelector("span").textContent = variant.available
      ? "Add to cart"
      : "Sold out";
  }

  function closeModal() {
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;

    modal.remove();
    document.documentElement.classList.remove("ks-cart-quickview-open");
    activeProduct = null;
    activeVariant = null;
    selectedOptions = [];
    lastFocusedElement?.focus?.();
  }

  function bindModal(modal) {
    setSwatchColors(modal);
    updateModalVariant(modal, activeVariant);

    modal.addEventListener("click", (event) => {
      if (event.target === modal || event.target.closest("[data-ks-qv-close]")) {
        closeModal();
        return;
      }

      const option = event.target.closest(".ks-cart-qv-option");
      if (option && !option.disabled) {
        const optionIndex = Number(option.dataset.optionIndex);
        const requestedValue = option.dataset.value;
        selectedOptions[optionIndex] = requestedValue;
        updateModalVariant(
          modal,
          bestVariant(activeProduct, optionIndex, requestedValue)
        );
        return;
      }

      const input = modal.querySelector(".quantity__input");
      if (event.target.closest("[data-ks-qv-minus]")) {
        input.value = Math.max(1, Number(input.value || 1) - 1);
      }
      if (event.target.closest("[data-ks-qv-plus]")) {
        input.value = Math.max(1, Number(input.value || 1) + 1);
      }
    });

    modal.querySelector("form").addEventListener("submit", async (event) => {
      event.preventDefault();

      const form = event.currentTarget;
      const button = form.querySelector("[type='submit']");
      const error = modal.querySelector(".ks-cart-qv-error");
      error?.remove();

      if (!activeVariant?.available || button.disabled) return;

      button.disabled = true;
      button.classList.add("btn-loading");
      button.querySelector("span").textContent = "Adding…";

      try {
        const formData = new FormData(form);
        formData.set("id", String(activeVariant.id));

        const response = await fetch(cartAddUrl(), {
          method: "POST",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          body: formData
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.status || result.errors) {
          throw new Error(
            result.description ||
              result.message ||
              "This item could not be added to your cart."
          );
        }

        sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
        window.location.reload();
      } catch (errorValue) {
        const message = document.createElement("div");
        message.className = "ks-cart-qv-error";
        message.setAttribute("role", "alert");
        message.textContent =
          errorValue.message || "This item could not be added to your cart.";
        button.insertAdjacentElement("beforebegin", message);
        button.disabled = false;
        button.classList.remove("btn-loading");
        button.querySelector("span").textContent = "Add to cart";
      }
    });
  }

  async function openModal(handle, card, trigger) {
    setCardLoading(card, true);
    lastFocusedElement = trigger;

    try {
      const product = await getProduct(handle);
      const variant = firstAvailableVariant(product);
      if (!variant) throw new Error("No purchasable variant was found.");

      closeModal();
      activeProduct = product;
      activeVariant = variant;
      selectedOptions = variantOptions(variant);

      document.body.insertAdjacentHTML("beforeend", modalMarkup(product, variant));
      document.documentElement.classList.add("ks-cart-quickview-open");

      const modal = document.getElementById(MODAL_ID);
      bindModal(modal);
      modal.querySelector("[data-ks-qv-close]")?.focus();
    } catch (error) {
      console.error("[KS cart quick view]", error);
      window.alert("Product options could not be opened. Please try again.");
    } finally {
      setCardLoading(card, false);
    }
  }

  function restoreScroll() {
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (saved === null) return;
    sessionStorage.removeItem(SCROLL_KEY);
    requestAnimationFrame(() => {
      window.scrollTo({ top: Number(saved) || 0, left: 0, behavior: "auto" });
    });
  }

  document.addEventListener(
    "click",
    (event) => {
      const cart = document.querySelector(CART_ROOT);
      const trigger = event.target.closest(TRIGGERS);
      if (!cart || !trigger || !cart.contains(trigger)) return;

      if (
        event.button > 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const card = trigger.closest(CARD);
      const handle = getHandle(card, trigger);
      if (!card || !handle) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      openModal(handle, card, trigger);
    },
    true
  );

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && document.getElementById(MODAL_ID)) {
      closeModal();
    }
  });

  document.addEventListener("DOMContentLoaded", () => {
    prepareCards(document);
    restoreScroll();
  });

  prepareCards(document);
})();
